import React from 'react';
import Icon from 'Components/UI/Icon';
import MaterialIcon from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Icon';
import moment from 'moment';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { addSpacesAndCapitalizeAllWords } from 'Helpers/Formats';
import { householdSecureFilesPreviewUri } from 'Consts/sharedUris';
import modalConsts from 'Consts/ModalNames';
import { makeStyles } from '@material-ui/core';
import { openModal } from 'Helpers/ModalHelper';

import {
  DOCUMENT_UPLOAD,
  PLAID_FAQ_NOTIFICATION,
  KISSFLOW_NOTIFICATION,
  TASK_ASSIGNED_NOTIFICATION,
  TASK_STATUS_CHANGE_NOTIFICATION,
  NEW_MESSAGE_NOTIFICATION,
  NEW_FEED_EMAIL_MESSAGE_NOTIFICATION,
  NEW_FEED_TASK_MESSAGE_NOTIFICATION,
  EMOJI_NOTIFICATION,
  ADDED_TO_THREAD_NOTIFICATION,
  NEW_SCENARIO_COMMENT_NOTIFICATION,
} from 'Consts/NotificationTypes';
import PropTypes from 'prop-types';
import {
  advisorPageMessages, clientDashboardPath2, clientPageMessages,
} from 'Consts/Strings';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { buildPageLink } from 'Views/Page/Content/Templates';
import NotificationListLink from './NotificationListLink';

moment.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s',
    s(number) {
      return `${number}s`;
    },
    m: '1min',
    mm(number) {
      return `${number}min`;
    },
    h: '1h',
    hh: '%dh',
    d: '1d',
    dd: '%dd',
    M: '1m',
    MM: '%dm',
    y: '1y',
    yy: '%dy',
  },
});
// Icon
const DEFAULT_ICON = 'https://static.facetwealth.com/icon/file.svg';

const useStyles = makeStyles(theme => ({
  arrow: {
    color: theme.palette.primary.main,
    justifyContent: 'flex-end',
    fontSize: 12,
    lineHeight: 2,
    minWidth: 0,
    width: theme.spacing(6.75),
  },
  time: {
    minWidth: '56px',
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(1),
  },
  description: {
    whiteSpace: 'pre',
  },
  checkIcon: {},
}));

export default function NotificationListItem({
  notification,
  onClick,
  className,
  isAgent,
}) {
  const classes = useStyles();

  const {
    relationships,
    related,
    createdBy,
    createdAt,
    count,
    description,
    url,
    householdId,
    fileId,
    actionItemId,
    type: notificationType,
  } = notification;

  const {
    icon: {
      links: { self: iconLink },
    },
    href: {
      links: { self: hrefLink },
    },
  } = relationships;

  const getResourceAndContent = () => {
    switch (notificationType) {
      case DOCUMENT_UPLOAD: {
        const fileIds = related
          ? [fileId, ...related.map(r => r.fileId)]
          : [fileId];
        return [
          {
            link: householdSecureFilesPreviewUri(householdId, fileIds),
          },
          count > 1
            ? `${createdBy} uploaded ${count} files [${householdId}]`
            : `${createdBy} uploaded a doc [${householdId}]`,
        ];
      }
      case PLAID_FAQ_NOTIFICATION:
        return [
          { href: hrefLink },
          description || addSpacesAndCapitalizeAllWords(notificationType),
        ];
      case KISSFLOW_NOTIFICATION:
        return [{ href: url }, description];
      case TASK_STATUS_CHANGE_NOTIFICATION: {
        return [{
          link: '#',
          modal: {
            name: modalConsts.simpleActionItemModal,
            searchParams: {
              todoId: actionItemId,
              householdId,
            },
          },
        }, description];
      }
      case TASK_ASSIGNED_NOTIFICATION: {
        const taskLink = `${clientDashboardPath2}?${modalConsts.modal}=${modalConsts.actionItemList}#ai-dialog-${actionItemId}`;
        if (isAgent) {
          return [{
            link: '#',
            modal: {
              name: modalConsts.simpleActionItemModal,
              searchParams: {
                todoId: actionItemId,
                householdId,
              },
            },
          }, description];
        }

        return [{ link: taskLink }, description];
      }
      case NEW_MESSAGE_NOTIFICATION:
      case EMOJI_NOTIFICATION:
      case ADDED_TO_THREAD_NOTIFICATION:
      case NEW_FEED_TASK_MESSAGE_NOTIFICATION:
      case NEW_SCENARIO_COMMENT_NOTIFICATION:
      case NEW_FEED_EMAIL_MESSAGE_NOTIFICATION: {
        let link = '';
        if (relationships.messagesPage) {
          link = buildPageLink(
            relationships.messagesPage?.links.related,
            isAgent ? advisorPageMessages : clientPageMessages,
            { threadSelfLink: relationships.thread ? relationships.thread?.links.related : '' }, // params
          );
        }
        return [{ link }, description];
      }
      default:
        return [
          { href: window.location.href },
          description || addSpacesAndCapitalizeAllWords(notificationType),
        ];
    }
  };

  const [resource, text] = getResourceAndContent();

  const now = moment(Date.now());
  const notificationTime = moment(createdAt);
  const aWeekAgo = now.unix() - now.subtract(1, 'week').unix();
  const notificationTimeDiff = now.unix() - notificationTime.unix();

  const timestamp = notificationTimeDiff > aWeekAgo
    ? notificationTime.format('MM/DD/YY')
    : notificationTime.from(Date.now());

  const handleClick = (...args) => {
    if (resource.modal) {
      openModal(resource.modal.name, Object.entries(resource.modal.searchParams).map(([k, v]) => ({ [k]: v })));
    }
    return onClick(...args);
  };
  return (
    <NotificationListLink className={className} onClick={handleClick} {...resource}>
      <ListItemIcon>
        <Icon src={iconLink || DEFAULT_ICON} height="32px" width="32px" />
      </ListItemIcon>
      <Typography align="left" variant="body1" noWrap>
        {text}
      </Typography>
      <ListItemText align="right" variant="body1" className={classes.time}>
        {timestamp}
      </ListItemText>
      {
        (resource.completed) ? (
          <ListItemIcon className={classes.arrow}>
            <MaterialIcon
              className={classes.checkIcon}
              edge="end"
              fill={'success'}
              icon={CheckCircleIcon}
              stroke={'success'}
            />
          </ListItemIcon>
        ) : (
          <ListItemIcon className={classes.arrow}>
            View <ChevronRightIcon />
          </ListItemIcon>
        )
      }
    </NotificationListLink>
  );
}

NotificationListItem.defaultProps = {
  className: '',
};

NotificationListItem.propTypes = {
  isAgent: PropTypes.bool.isRequired,
  notification: PropTypes.object.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
};
